import { forwardRef, type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  progressSizeStyles,
  getProgressBarClass,
  toPercentage,
  type ProgressSize,
  type ProgressVariant,
} from './progress/progressStyles';

// Oeffentlicher Einstiegspunkt: `Stepper` samt Typen wird hier weiterexportiert,
// damit der Importpfad `@/components/ui` unveraendert bleibt.
export { Stepper } from './progress/Stepper';
export type { Step, StepperProps } from './progress/stepperTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Props for the Progress component
 */
export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** Current progress value (0-100) */
  value: number;
  /** Maximum value */
  max?: number;
  /** Size variant */
  size?: ProgressSize;
  /** Visual variant */
  variant?: ProgressVariant;
  /** Show percentage label */
  showLabel?: boolean;
  /** Custom color */
  color?: string;
  /** Indeterminate state (loading) */
  indeterminate?: boolean;
  /** Animation on value change */
  animated?: boolean;
}

/**
 * Progress component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Progress value={75} showLabel />
 *
 * <Progress value={50} variant="gradient" size="lg" />
 *
 * <Progress indeterminate />
 * ```
 */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      value,
      max = 100,
      size = 'md',
      variant = 'default',
      showLabel = false,
      color = 'var(--color-primary)',
      indeterminate = false,
      animated = true,
      className,
      ...props
    },
    ref
  ) => {
    const percentage = toPercentage(value, max);
    const sizes = progressSizeStyles[size];
    const barClass = getProgressBarClass(variant, animated);

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        {/* Label */}
        {showLabel && !indeterminate && (
          <div className="flex justify-between items-center mb-1">
            <span className={cn(sizes.label, 'text-surface-700 font-medium')}>Progress</span>
            <span className={cn(sizes.label, 'text-surface-500')}>{Math.round(percentage)}%</span>
          </div>
        )}

        {/* Track */}
        <div
          className={cn(
            sizes.track,
            'w-full overflow-hidden',
            'backdrop-blur-md',
            'bg-white/[0.08]',
            'border border-white/[0.10]'
          )}
          role="progressbar"
          aria-valuenow={indeterminate ? undefined : value}
          aria-valuemin={0}
          aria-valuemax={max}
        >
          {indeterminate ? (
            // Indeterminate animation
            <motion.div
              className={cn(sizes.track, 'h-full', barClass)}
              style={{
                backgroundColor: variant === 'default' ? color : undefined,
                width: '40%',
              }}
              animate={{
                x: ['-100%', '350%'],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ) : (
            // Determinate progress
            <motion.div
              className={cn(sizes.track, 'h-full', barClass)}
              style={{
                backgroundColor: variant === 'default' ? color : undefined,
                boxShadow: `0 0 10px ${color}50`,
              }}
              initial={animated ? { width: 0 } : false}
              animate={{ width: `${percentage}%` }}
              transition={{
                duration: animated ? 0.5 : 0,
                ease: 'easeOut',
              }}
            />
          )}
        </div>
      </div>
    );
  }
);

Progress.displayName = 'Progress';

export default Progress;
